/**
 * Pixel/secret guard for telemetry (FR-006, SC-004).
 *
 * The cloud rejects pixel-bearing payloads (413/422), but the client MUST NOT
 * even attempt to send them. This guard scans any telemetry payload for binary
 * blobs (ArrayBuffer/TypedArray/Blob), data: image URLs, base64-ish image
 * fields, and obvious secret keys, and throws before transmit/buffer.
 */

const SECRET_KEY_RE = /(password|secret|token|api[_-]?key|authorization|bearer)/i;
const PIXEL_KEY_RE = /(pixels?|imagebytes|image_bytes|imagedata|image_data|bitmap|rgba?|bytes)$/i;
const DATA_IMAGE_RE = /^data:image\//i;

export class TelemetryPixelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TelemetryPixelError";
  }
}

function isBinary(v: unknown): boolean {
  return (
    v instanceof ArrayBuffer ||
    ArrayBuffer.isView(v) ||
    (typeof Blob !== "undefined" && v instanceof Blob) ||
    (typeof globalThis.File !== "undefined" && v instanceof globalThis.File)
  );
}

/** Heuristic: a long base64-looking string is treated as smuggled pixels. */
function looksLikeImageBase64(v: string): boolean {
  if (DATA_IMAGE_RE.test(v)) return true;
  if (v.length > 1024 && /^[A-Za-z0-9+/=\r\n]+$/.test(v)) return true;
  return false;
}

export function assertNoPixels(payload: unknown, path = "$"): void {
  if (payload == null) return;

  if (isBinary(payload)) {
    throw new TelemetryPixelError(`Telemetry payload contains binary/image data at ${path}`);
  }
  if (typeof payload === "string") {
    if (looksLikeImageBase64(payload)) {
      throw new TelemetryPixelError(`Telemetry payload contains image-like data at ${path}`);
    }
    return;
  }
  if (typeof payload !== "object") return;

  if (Array.isArray(payload)) {
    payload.forEach((item, i) => assertNoPixels(item, `${path}[${i}]`));
    return;
  }

  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    if (SECRET_KEY_RE.test(key)) {
      throw new TelemetryPixelError(`Telemetry payload contains a secret-like field "${key}" at ${path}`);
    }
    if (PIXEL_KEY_RE.test(key) && (isBinary(value) || Array.isArray(value))) {
      throw new TelemetryPixelError(`Telemetry payload contains a pixel-like field "${key}" at ${path}`);
    }
    assertNoPixels(value, `${path}.${key}`);
  }
}

/** Returns true when the payload is safe (metadata only). */
export function isPixelFree(payload: unknown): boolean {
  try {
    assertNoPixels(payload);
    return true;
  } catch {
    return false;
  }
}
